// Auth helpers for the re-engagement panel. Reuses the main nerdle auth backend
// (api.leaderboardle.com) exactly as the main game does (see nerdle's doLogin.ts /
// LBLLoginModal.tsx). On success the nerdle Bearer token + email are written to the
// shared nerdlegame.com localStorage (labs is the SAME origin as the main game), so both
// apps see the logged-in state. Google + email only — NO Facebook (product decision).

const AUTH = 'https://api.leaderboardle.com'

export type NerdleIdentity = { lblToken: string; email: string | null }

// Read an existing nerdle login from the shared localStorage. null if not logged in.
export function readNerdleIdentity(): NerdleIdentity | null {
  try {
    const lblToken = localStorage.getItem('lbl_token')
    if (!lblToken) return null
    return { lblToken, email: localStorage.getItem('userEmail') }
  } catch {
    return null
  }
}

export type AuthResult = { ok: boolean; email: string | null; message?: string }

// POST a login (/login) or create (/user/create) payload. On a token response, persist
// lbl_token + userEmail to localStorage (so labs AND the main game are logged in). The
// create path auto-logs-in on success, mirroring the main game.
export async function nerdleAuth(
  payload: Record<string, unknown>,
  opts: { signup?: boolean } = {}
): Promise<AuthResult> {
  const url = AUTH + (opts.signup ? '/user/create' : '/login')
  let json: any
  try {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(payload),
    })
    json = await res.json()
  } catch {
    return { ok: false, email: null, message: 'Network error — please try again.' }
  }

  if (json && json.token) {
    try {
      localStorage.setItem('lbl_token', json.token)
      if (typeof payload.email === 'string') localStorage.setItem('userEmail', payload.email)
    } catch {
      /* localStorage unavailable — token still returned to caller */
    }
    return { ok: true, email: typeof payload.email === 'string' ? payload.email : null }
  }

  // create succeeded but returned no token → log in with the same credentials
  if (opts.signup && json && (json.item === 'Success' || json.message === 'User is created')) {
    return nerdleAuth({ email: payload.email, password: payload.password, source: 'nerdle' }, {})
  }

  // the account already exists (the API returns 404 + "already registered"). Try logging
  // the returning user in with the same credentials; if that fails — e.g. it's a Google
  // account with no password — guide them to the Google button rather than a raw error.
  if (opts.signup && json && typeof json.message === 'string' && /already|registered|in use/i.test(json.message)) {
    const login = await nerdleAuth({ email: payload.email, password: payload.password, source: 'nerdle' }, {})
    if (login.ok) return login
    return {
      ok: false,
      email: null,
      message: 'This email already has a nerdle account — try "Continue with Google", or use a different email.',
    }
  }

  return { ok: false, email: null, message: (json && json.message) || 'Sign-in failed. Please try again.' }
}
